<template>
  <div class="voucher-area" :class="{ 'h-100': orderDetails.length === 0 }">
    <div
      class="bg-green px-4 pt-5 border-right border-light h-100 voucher-area-left"
      :class="{ expanded: orderDetails.length === 0 }"
    >
      <voucher-code-form @pressed-activate="pressedActivate" />
    </div>

    <div
      id="voucherList"
      class="voucher-area-right overflow-hidden"
      :class="{ visible: orderDetails.length > 0 }"
    >
      <h3 v-if="orderDetails.length" class="mb-4 pt-5 d-flex justify-content-between">
        <span>Løs inn verdibevis</span>
      </h3>
      <p class="lead" v-if="invoice_details_count === 0">
        Verdibeviset er gyldig og kan løses inn. Oppgi gjennomføringsdatoen i steg 2 og gå videre
        til steg 3 for innløsing av verdibeviset.
      </p>
      <p class="mb-4 font-weight-bold">2. Oppgi gjennomføringsdato</p>

      <div v-if="orderDetails.length" class="p-3 mb-2 rounded bg-light">
        <div class="row">
          <div class="col-12 col-sm-6 d-flex align-items-center">
            <div class="form-group w-100">
              <date-picker
                ref="datepicker"
                input-class="form-control-lg form-control w-100"
                :popupStyle="{
                  'border-radius': '8px',
                }"
                class="w-100"
                :lang="lang"
                :first-day-of-week="1"
                :value="experienceDate"
                :minute-step="10"
                type="datetime"
                format="DD-MM-YYYY HH:mm"
                @change="selectedDate"
                @panel-change="panelChanged"
                @clear="hideCalendarButton = false"
              ></date-picker>
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="text-small" v-if="experiencedAtDates().past_30">
              <small
                :class="{
                  'text-danger': experiencedAtDates().past_30_percentage <= 60,
                  'text-info':
                    experiencedAtDates().past_30_percentage > 60 &&
                    experiencedAtDates().past_30_percentage <= 80,
                  'text-primary': experiencedAtDates().past_30_percentage > 80,
                }"
                >{{ experiencedAtDates().past_30 }}</small
              >
              <i
                class="fa fa-info-circle hoverable"
                v-b-tooltip.hover
                :title="`De siste 30 dager har dere ${
                  experiencedAtDates().past_30_percentage < 80 ? ' kun' : ''
                } oppgitt dato for ${experiencedAtDates().top_30_with_dates} av ${
                  experiencedAtDates().top_30_total
                } innløste verdibevis. Oppgi dato hver gang for å få flere brukeromtaler.`"
              ></i>
            </div>
            <div class="text-small" v-else-if="experiencedAtDates().all">
              <small
                :class="{
                  'text-danger': experiencedAtDates().allPercentage <= 60,
                  'text-info':
                    experiencedAtDates().allPercentage > 60 &&
                    experiencedAtDates().allPercentage <= 80,
                  'text-primary': experiencedAtDates().allPercentage > 80,
                }"
                >{{ experiencedAtDates().all }}
                <i
                  class="fa fa-info-circle hoverable"
                  v-b-tooltip.hover
                  :title="`Det siste halvåret har dere ${
                    experiencedAtDates().allPercentage < 80 ? ' kun' : ''
                  } oppgitt dato for ${experiencedAtDates().allWithDates} av ${
                    experiencedAtDates().allTotal
                  } innløste verdibevis. Oppgi dato hver gang for å få flere brukeromtaler.`"
                ></i
              ></small>
            </div>
            <small class="text-muted">
              Oppgi tidspunkt for gjennomføring av opplevelsen, så innhenter vi brukeromtaler. Gode
              brukeromtaler øker salget av dine opplevelser.
            </small>
          </div>
        </div>
      </div>
      <small class="text-success" v-if="experienceDate">
        Flott, dagen etter gjennomføring ber vi kunden gi dere en brukeromtale.
      </small>
      <b-alert
        :show="errorMessage.length > 0"
        variant="danger"
        dismissible
        @dismissed="resetErrors"
      >
        {{ errorMessage }}
      </b-alert>
      <span v-if="orderDetails.length" class="mb-2 mt-3 d-flex justify-content-between">
        <p class="font-weight-bold">3. Løs inn verdibevis:</p>
        <button
          @click="useAll()"
          v-if="
            orderDetails.length > 1 &&
            orderDetails.filter(od => od.used_at === null).length === orderDetails.length
          "
          class="btn btn-primary"
        >
          Løs in alle
        </button>
      </span>
      <p v-if="invoice_details_count === 0">
        Innløste verdibevis listes under fanen «Oppgjør» i menyen. For å motta betaling må du
        navigere til «Oppgjør» og huke av de verdibevis du vil sende til oppgjør. Verdibevis som er
        markert og sendt til oppgjør får du betalt for innen den 15. i påløpende måned.
      </p>
      <div v-for="orderDetail in orderDetails" :key="orderDetail.id">
        <voucher-box
          :order-detail="orderDetail"
          :product="product"
          :experienced_date="experienced_at"
          @use="useVoucherFun"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VoucherCodeForm from '@/components/VoucherCodeForm.vue';
import VoucherBox from '@/components/VoucherBox.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/nb';

import dayjs from 'dayjs';

export default {
  name: 'home',
  components: {
    VoucherCodeForm,
    VoucherBox,
    DatePicker,
  },
  computed: {
    ...mapGetters({
      errorMessage: 'vouchers/error',
      experienceDate: 'vouchers/experienced_at',
      orderDetails: 'vouchers/orderDetails',
      invoice_details: 'vouchers/invoice_details',
      invoice_details_count: 'vouchers/invoice_details_count',
      product: 'vouchers/product',
    }),
  },
  data() {
    return {
      successMessage: '',
      experienced_at: this.experienceDate,
      hideCalendarButton: this.experienced_date !== undefined,
      timePickerOptions: {
        start: '00:00',
        step: '00:30',
        end: '23:30',
      },
      lang: {
        days: ['Sø', 'Ma', 'Ti', 'On', 'To', 'Fr', 'Lø'],
        months: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'Mai',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Okt',
          'Nov',
          'Dec',
        ],
        pickers: ['next 7 days', 'next 30 days', 'previous 7 days', 'previous 30 days'],
        placeholder: {
          date: 'Velg dato',
          dateRange: 'Select Date Range',
        },
      },
    };
  },
  mounted() {
    this.getInvoiceDetailsCount();
  },
  methods: {
    ...mapActions({
      validateCode: 'vouchers/validate',
      useVoucher: 'vouchers/use',
      getInvoiceDetailsCount: 'vouchers/getInvoiceDetailsCount',
    }),
    ...mapMutations({
      resetErrors: 'vouchers/reset_errors',
      setExperiencedAt: 'vouchers/set_experienced_at',
    }),
    selectedDate(date) {
      this.setExperiencedAt(date);
    },
    panelChanged(date) {
      if (date === 'TIME') {
        this.setExperiencedAt(this.experienceDate.setHours(16));
      }
    },
    useAll() {
      this.orderDetails.forEach(orderDetail => {
        this.useVoucherFun(orderDetail);
        bus.$emit('clearTypeahead');
      });
    },
    pressedActivate(code) {
      this.validateCode({ code: code, errors: true }).then(_ => {
        this.$scrollTo('#voucherList', 500, { offset: -30 });
      });
    },
    openDatePicker() {
      this.$refs.datepicker.showPopup();
      this.hideCalendarButton = true;
    },
    useVoucherFun(orderDetail) {
      //if (this.orderDetails.length < 2) {
      bus.$emit('clearTypeahead');
      //}
      this.useVoucher(orderDetail).then(res => {
        this.$bvToast.toast(res.success, {
          variant: 'success',
          title: `Verdibevis #${orderDetail.code}`,
          autoHideDelay: 5000,
        });
      });
    },
    experiencedAtDates() {
      if (this.invoice_details === null) {
        return {
          allCount: 0,
          all: null,
          past_30: null,
        };
      }

      let details = this.invoice_details;

      let sinceAddedExperienced = details.filter(d =>
        dayjs(d.created_at).isAfter(dayjs().subtract(6, 'months'))
      );

      let sinceAddedExperiencedCount = sinceAddedExperienced.length;
      let sinceAddedExperiencedWithDates = sinceAddedExperienced.filter(
        d => d.experienced_at !== null
      );

      let since30days = details.filter(d =>
        dayjs(d.created_at).isAfter(dayjs().subtract(30, 'days'))
      );

      let since30daysCount = since30days.length;
      let since30daysWithDates = since30days.filter(d => d.experienced_at !== null);

      return {
        all:
          sinceAddedExperienced.length >= 20
            ? `Du mangler ${
                sinceAddedExperiencedCount - sinceAddedExperiencedWithDates.length
              } av ${sinceAddedExperienced.length}`
            : null,
        allPercentage: Math.round(
          (sinceAddedExperiencedWithDates.length / sinceAddedExperienced.length) * 100
        ),
        allWithDates: sinceAddedExperiencedWithDates.length,
        allTotal: sinceAddedExperienced.length,
        past_30:
          since30days.length >= 20
            ? `Du mangler (${since30daysCount - since30daysWithDates.length} av ${
                since30days.length
              })`
            : null,
        past_30_percentage: Math.round((since30daysWithDates.length / since30days.length) * 100),
        past_30_with_dates: sinceAddedExperiencedWithDates.length,
        past_30_total: sinceAddedExperienced.length,
      };
    },
  },
};
</script>

<style>
</style>

<style scoped>
.voucher-area {
  display: flex;
  flex-direction: column;
  margin-left: -15px;
  margin-right: -15px;
}

@media (min-width: 768px) {
  .voucher-area {
    margin-left: -30px;
  }
}

@media (min-width: 992px) {
  .voucher-area {
    height: 100%;
    flex-direction: row;
  }

  .voucher-area-left {
    max-width: 35%;
  }

  .voucher-area-left.expanded {
    max-width: 50%;
  }
}

.slide-leave-active,
.slide-enter-active {
  transition: 0.3s;
}
.slide-enter {
  transform: translate(-100%, 0);
}
.slide-leave-to {
  transform: translate(100%, 0);
}

.voucher-area-left {
  transition: max-width 0.3s ease-in-out;
}

.voucher-area-right {
  max-width: 100%;
  margin-left: 30px;
  margin-right: 30px;
  opacity: 0;
  transition: width 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.voucher-area-right.visible {
  opacity: 1;
}

.hoverable {
  cursor: pointer;
}
</style>
